<script lang="ts">
    import { EventCalendar } from '@/components/calendar';
    import type { CalendarEvent, CalendarView, KeyItem } from '@/types/calendar';
    import axios from 'axios';

    type StatusOption = {
        key: string;
        label: string;
        color: string;
        cssClass: string;
    };

    type Props = {
        statuses: StatusOption[];
        stickyTopOffset?: number;
    };

    let { statuses, stickyTopOffset = 0 }: Props = $props();

    // Calendar view persistence (day/week/month)
    const STORAGE_KEY = 'leads-calendar-view';
    const validViews: CalendarView[] = ['day', 'week', 'month'];

    function getInitialView(): CalendarView {
        if (typeof window === 'undefined') return 'month';
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored && validViews.includes(stored as CalendarView) ? (stored as CalendarView) : 'month';
    }

    let calendarView = $state<CalendarView>(getInitialView());

    // Calendar state
    let events = $state<CalendarEvent[]>([]);
    let isLoading = $state(false);
    let activeStatusFilters = $state<string[]>([]);
    let currentDateRange = $state<{ start: string; end: string } | null>(null);

    // Statuses excluded from calendar legend (no dates)
    const excludedStatuses = ['new', 'needs_survey', 'needs_pricing', 'archived'];

    let legendItems = $derived<KeyItem[]>([
        ...statuses
            .filter((s) => !excludedStatuses.includes(s.key))
            .map((s) => ({
                key: s.key,
                color: (s.color || 'gray') as KeyItem['color'],
                label: s.label,
                variant: 'solid' as const,
            })),
        {
            key: 'original_hire',
            color: 'teal' as KeyItem['color'],
            label: 'Original Hire',
            variant: 'solid' as const,
        },
    ]);

    // Linked filters: original_hire and extra_hire toggle together
    const linkedFilterKeys: Record<string, string> = {
        original_hire: 'extra_hire',
        extra_hire: 'original_hire',
    };

    function handleFilterToggle(key: string) {
        const linked = linkedFilterKeys[key];
        if (activeStatusFilters.includes(key)) {
            activeStatusFilters = activeStatusFilters.filter((k) => k !== key && k !== linked);
        } else {
            const toAdd = linked ? [key, linked] : [key];
            activeStatusFilters = [...activeStatusFilters, ...toAdd.filter((k) => !activeStatusFilters.includes(k))];
        }
    }

    function handleFilterReset() {
        activeStatusFilters = [];
    }

    function getApiStatuses(): string[] {
        return [...new Set(activeStatusFilters.map((k) => (k === 'original_hire' ? 'extra_hire' : k)))];
    }

    // Refetch when filters change
    let previousFilters = $state<string[]>([]);
    $effect(() => {
        const current = activeStatusFilters;
        if (currentDateRange && JSON.stringify(current) !== JSON.stringify(previousFilters)) {
            previousFilters = [...current];
            fetchEvents(currentDateRange.start, currentDateRange.end);
        }
    });

    async function fetchEvents(startDate: string, endDate: string) {
        currentDateRange = { start: startDate, end: endDate };
        const apiStatuses = getApiStatuses();
        isLoading = true;
        try {
            const params: Record<string, unknown> = { start_date: startDate, end_date: endDate };
            if (apiStatuses.length > 0) {
                params.status = apiStatuses;
            }
            const response = await axios.get<CalendarEvent[]>('/web-api/calendar/events', { params });
            events = response.data;
        } catch (error) {
            console.error('Failed to fetch calendar events:', error);
            events = [];
        } finally {
            isLoading = false;
        }
    }
</script>

<div class="calendar-wrapper">
    <EventCalendar
        {events}
        defaultView={calendarView}
        {isLoading}
        {legendItems}
        activeFilterKeys={activeStatusFilters}
        onFilterToggle={handleFilterToggle}
        onFilterReset={handleFilterReset}
        showPopover={true}
        {stickyTopOffset}
        onDateRangeChange={fetchEvents}
        onViewChange={(view) => {
            calendarView = view;
            sessionStorage.setItem(STORAGE_KEY, view);
        }}
    />
</div>

<style>
    .calendar-wrapper {
        min-height: 600px;
    }
</style>